import React, { useEffect, useState } from 'react';
import { Tabs, Tooltip } from 'freemium-ui';
import { motion, AnimatePresence } from 'framer-motion';
import { useFormContext } from 'react-hook-form';
import { HookFormEditable } from 'components/editable-input';
import Icons from 'components/icons';
import { ACTIVE_OPTIONS as OPTIONS, RATING_TABS } from '../../../constants';
import BlockCustomize from './block-customize';
import CustomizeNPS from './nps-customize';
import Question from '../question';
import BlockConfiguration from './block-configuration';
import { useProductConfigContext } from 'shared/product-config-provider';

export type BlockProps = {
  block: any;
  open: boolean;
  setOpen: any;
  surveyForm: any;
  blocks: any;
  disabled?: boolean;
  setSurveyForm: Function;
  thankYouMessage?: boolean;
};

const Block = ({
  block,
  disabled,
  setOpen,
  surveyForm,
  setSurveyForm,
  thankYouMessage,
  blocks,
  open,
}: BlockProps) => {
  const {
    watch,
    formState: { errors },
  } = useFormContext();
  const productConfig = useProductConfigContext();
  // const questionsWatch = watch('question_details.questions')
  const blockQuestionIndex = surveyForm.question_details?.questions?.findIndex(
    (question: any) => block?.primary_question_name === question.name,
  );

  const [activeRatingTab, setActiveRatingTab] = React.useState(
    RATING_TABS[0].id,
  );

  const activeQuestion = block?.is_based_on_rating
    ? RATING_TABS.findIndex((s: any) => s.id === activeRatingTab)
    : 0;

  // console.log('surveyForm.meta.blocks', block)

  const currentOption = OPTIONS.find(
    (option: any) => option.id === block?.type,
  );

  const hasError =
    errors?.question_details?.questions &&
    errors?.question_details?.questions[blockQuestionIndex];

  const [isHighlighted, setIsHighlighted] = useState(false);
  useEffect(() => {
    if (!isHighlighted && open) {
      setIsHighlighted(true);
      setTimeout(() => {
        setIsHighlighted(false);
      }, 1500);
    }

    return () => {
      setIsHighlighted(false);
    };
  }, [open]);

  return (
    <div
      className={`section ${isHighlighted ? 'highlighted ' : ''}${
        hasError && Object.keys(hasError) ? 'has-error ' : ''
      }${open ? 'open' : ''}`.trim()}
      onClick={setOpen}
    >
      <div className="section-header">
        {/* to avoid the parent opacity from appyling to this icon, have a separate div wrapper */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div className="section-title">
            {!open && !thankYouMessage && block?.type !== 'RANGE' && (
              <>
                <Icons
                  size="18"
                  viewBox={currentOption?.viewBox || '0 0 18 18'}
                  fill="none"
                  glyph={currentOption?.icon}
                />
                <span className="section-type">
                  {/* {block?.type !== 'RANGE' && <>&nbsp;-&nbsp;</>} */}
                  {currentOption?.label || ''}
                </span>
              </>
            )}
            <span className="question-identifier-section">
              <HookFormEditable
                name={`question_details.questions[${blockQuestionIndex}].type_info.meta.block.title`}
                placeholder="Enter question identifier"
                disabled={disabled}
                defaultValue={block?.title}
                maxCount={15}
              />
            </span>
            {!thankYouMessage && block?.type !== 'RANGE' && block.required && (
              <span className="section-required">Required</span>
            )}
          </div>
          <div>
            {productConfig?.enableAnalyticsPivot &&
              watch(
                `question_details.questions[${blockQuestionIndex}].type_info.${
                  block?.type === 'PARAGRAPH' ? 'comment' : 'driver'
                }`,
              ) &&
              ['PARAGRAPH', 'DROPDOWN', 'MULTI_SELECT'].indexOf(block?.type) >
                -1 && (
                <Tooltip label="Use this answer for analytics" position="right">
                  <span className="section-required report">
                    <Icons glyph="graph" size="12" viewBox="0 0 13 13" />
                  </span>
                </Tooltip>
              )}
          </div>
        </div>
        {open && !thankYouMessage && (
          <div className="section-action">
            {block?.type === 'RANGE' ? (
              <CustomizeNPS
                block={block}
                blocks={blocks}
                surveyForm={surveyForm}
                values={surveyForm.question_details?.questions?.find(
                  (question: any) =>
                    question.name === block.question_names[activeQuestion],
                )}
                setValues={(question: any) => {
                  setSurveyForm({
                    ...surveyForm,
                    question_details: {
                      ...surveyForm.question_details,
                      questions: surveyForm.question_details?.questions?.map(
                        (q: any) =>
                          q.name === block.question_names[activeQuestion]
                            ? { ...q, ...question }
                            : q,
                      ),
                    },
                  });
                }}
              />
            ) : (
              <BlockCustomize
                open={open}
                block={block}
                blocks={blocks}
                setSurveyForm={setSurveyForm}
                surveyForm={surveyForm}
                activeQuestion={activeQuestion}
              />
            )}
          </div>
        )}
      </div>

      {open && block?.type !== 'RANGE' && (
        <BlockConfiguration
          blockQuestionIndex={blockQuestionIndex}
          block={block}
          disabled={disabled}
          setSurveyForm={setSurveyForm}
          thankYouMessage={thankYouMessage}
        />
      )}

      {open && block?.is_based_on_rating && (
        <div className="section-question">
          <div className="rating-tabs">
            <Tabs
              value={activeRatingTab}
              onChange={(e: any, v: any) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveRatingTab(v.id);
              }}
              options={RATING_TABS}
            />
          </div>
        </div>
      )}
      <AnimatePresence exitBeforeEnter>
        <motion.div
          key={`question-${activeQuestion}`}
          animate={{ opacity: 1, y: 0 }}
          initial={{ opacity: 0, y: 20 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.15 }}
        >
          <Question
            block={block}
            blockQuestionIndex={blockQuestionIndex}
            open={open}
            disabled={disabled}
            blocks={blocks}
            thankYouMessage={thankYouMessage}
            activeQuestion={activeQuestion}
          />
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Block;
